import React, { useState, useRef, useMemo } from 'react'

import Field from '../../_library/Field'
import Select from '../../_library/Select'
import MultiSelect from '../../_library/MultiSelect'
import TagsField2 from '../../_library/TagsField2'

import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useVirtual } from 'react-virtual'

export const GuestTicketsTable = ({
  event,
  data,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  optionsTicketTypeNames,
  optionsAddons,
  handleMultiSelect,
  handleRecipientChangeTags,
  deleteRecipient,
  hasAttendeeTags,
}) => {
  const tableContainerRef = useRef(null)
  const [columnVisibility, setColumnVisibility] = useState({})

  const columns = useMemo(
    () =>
      [
        {
          accessorKey: 'first_name',
          header: () => <span>First Name</span>,
          cell: info => {
            const { index } = info.row
            return (
              <Field
                type="text"
                id={'recipients[' + index + '].first_name'}
                value={values.recipients[index].first_name}
                error={errors.recipients && errors.recipients[index] && errors.recipients[index].first_name}
                touched={
                  touched.recipients && touched.recipients[index] && touched.recipients[index].first_name
                }
                onChange={handleChange}
                onBlur={handleBlur}
              />
            )
          },
        },
        {
          accessorKey: 'last_name',
          header: () => <span>Last Name</span>,
          cell: info => {
            const { index } = info.row
            return (
              <Field
                type="text"
                id={'recipients[' + index + '].last_name'}
                value={values.recipients[index].last_name}
                error={errors.recipients && errors.recipients[index] && errors.recipients[index].last_name}
                touched={
                  touched.recipients && touched.recipients[index] && touched.recipients[index].last_name
                }
                onChange={handleChange}
                onBlur={handleBlur}
              />
            )
          },
        },
        {
          accessorKey: 'email',
          header: () => <span>Email</span>,
          cell: info => {
            const { index } = info.row
            return (
              <Field
                type="email"
                id={'recipients[' + index + '].email'}
                value={values.recipients[index].email}
                error={errors.recipients && errors.recipients[index] && errors.recipients[index].email}
                touched={touched.recipients && touched.recipients[index] && touched.recipients[index].email}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            )
          },
        },
        {
          accessorKey: 'quantity',
          header: () => <span>Number of Tickets</span>,
          cell: info => {
            const { index } = info.row
            return (
              <Field
                type="number"
                id={'recipients[' + index + '].quantity'}
                value={values.recipients[index].quantity}
                error={errors.recipients && errors.recipients[index] && errors.recipients[index].quantity}
                touched={
                  touched.recipients && touched.recipients[index] && touched.recipients[index].quantity
                }
                onChange={handleChange}
                onBlur={handleBlur}
              />
            )
          },
        },
        {
          accessorKey: 'ticketTypeName',
          header: () => <span>Type of Ticket</span>,
          cell: info => {
            const { index } = info.row
            return (
              <Select
                options={optionsTicketTypeNames}
                id={'recipients[' + index + '].ticketTypeName'}
                value={values.recipients[index].ticketTypeName}
                error={
                  errors.recipients && errors.recipients[index] && errors.recipients[index].ticketTypeName
                }
                touched={
                  touched.recipients && touched.recipients[index] && touched.recipients[index].ticketTypeName
                }
                onChange={handleChange}
                onBlur={handleBlur}
              />
            )
          },
        },
        {
          accessorKey: 'ticketAddOns',
          header: () => <span>Attach Add-Ons</span>,
          cell: info => {
            const { index } = info.row
            return (
              <MultiSelect
                options={optionsAddons}
                selectedValues={values.recipients[index].ticketAddOns}
                id={'recipients[' + index + '].ticketAddOns'}
                error={errors.recipients && errors.recipients[index] && errors.recipients[index].ticketAddOns}
                touched={
                  touched.recipients && touched.recipients[index] && touched.recipients[index].ticketAddOns
                }
                onSelect={handleMultiSelect}
                onRemove={handleMultiSelect}
                avoidHighlightFirstOption="true"
                displayValue="name"
                hidePlaceholder="true"
                emptyRecordMsg="No more add-ons available"
              />
            )
          },
        },
        {
          accessorKey: 'notes',
          header: () => <span>Notes</span>,
          cell: info => {
            const { index } = info.row
            return (
              <Field
                type="notes"
                id={'recipients[' + index + '].notes'}
                value={values.recipients[index].notes}
                error={errors.recipients && errors.recipients[index] && errors.recipients[index].notes}
                touched={touched.recipients && touched.recipients[index] && touched.recipients[index].notes}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            )
          },
        },
        hasAttendeeTags && {
          accessorKey: 'tags',
          header: () => <span>Tags</span>,
          cell: info => {
            const { index } = info.row
            return (
              <TagsField2
                label="Attendee Tags"
                id={'recipients[' + index + '].tags'}
                value={values.recipients[index].tags}
                tags={event.attendeeTags}
                onChange={() => handleRecipientChangeTags(index)}
              />
            )
          },
        },
        {
          size: 30,
          accessorKey: 'delete',
          header: () => <span />,
          cell: info => {
            const { index } = info.row
            return (
              <div aria-hidden className="delete-recipient" onClick={() => deleteRecipient(index)}>
                <i className="fa fa-trash" aria-hidden="true" />
              </div>
            )
          },
        },
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [errors.recipients, touched.recipients, values.recipients],
  )

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    debugTable: true,
    state: {
      columnVisibility,
    },
  })

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: 5,
    estimateSize: React.useCallback(() => 62, []),
  })
  const { virtualItems: virtualRows, totalSize } = rowVirtualizer
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0

  return (
    <div
      ref={tableContainerRef}
      style={{ height: 500, overflow: 'auto' }}
      className="recipients-table-container"
    >
      <table style={{ tableLayout: 'fixed', width: '100%' }} className="table recipients-table">
        <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{ width: header.getSize(), padding: '8px 20px' }}
                >
                  <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {paddingTop > 0 && (
            <tr>
              <td style={{ height: `${paddingTop}px` }} />
            </tr>
          )}
          {virtualRows.map(virtualRow => {
            const row = rows[virtualRow.index]
            return (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td style={{ padding: 6 }} key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            )
          })}
          {paddingBottom > 0 && (
            <tr>
              <td style={{ height: `${paddingBottom}px` }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
